import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { appointmentsAPI, consultationDocumentsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PatientMedicalRecord from '@/components/PatientMedicalRecord';
import AvailabilityManager from '@/components/AvailabilityManager';
import {
  Calendar,
  Clock,
  Video,
  Home,
  MapPin,
  Check,
  X,
  Loader2,
  Stethoscope,
  FileText,
  ClipboardList,
  Send,
  AlertCircle,
  CheckCircle2,
  User,
  Camera,
  Upload,
  Image,
  Trash2,
  FolderOpen,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const NEXT_APPOINTMENT_OPTIONS = [
  { value: '1_week', label: 'Dans 1 semaine' },
  { value: '1_month', label: 'Dans 1 mois' },
  { value: '3_months', label: 'Dans 3 mois' },
  { value: '6_months', label: 'Dans 6 mois' },
  { value: 'not_needed', label: 'Non nécessaire' }
];

const ProfessionalAppointments = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  
  // Complete appointment modal state
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [summaryForm, setSummaryForm] = useState({
    diagnosis: '',
    recommendations: '',
    treatment: '',
    next_appointment: '',
    private_notes: ''
  });
  
  // Document upload state
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Patient medical record modal
  const [showPatientRecord, setShowPatientRecord] = useState(false);
  const [selectedPatientForRecord, setSelectedPatientForRecord] = useState(null);

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    try {
      const response = await appointmentsAPI.getAll();
      setAppointments(response.data.appointments || []);
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast.error('Erreur lors du chargement des rendez-vous');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (appointmentId, status) => {
    try {
      await appointmentsAPI.updateStatus(appointmentId, status);
      toast.success(status === 'confirmed' ? 'Rendez-vous confirmé' : 'Rendez-vous annulé');
      loadAppointments();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const openCompleteModal = (appointment) => {
    setSelectedAppointment(appointment);
    setSummaryForm({
      diagnosis: '',
      recommendations: '',
      treatment: '',
      next_appointment: '',
      private_notes: ''
    });
    setUploadedDocs([]);
    setShowCompleteModal(true);
  };

  // Camera functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Use back camera on mobile
      });
      setCameraStream(stream);
      setShowCamera(true);
      
      // Wait for videoRef to be available
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Impossible d\'accéder à la caméra. Vérifiez les permissions.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    
    canvas.toBlob(async (blob) => {
      if (blob) {
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        await uploadDocument(file, `Ordonnance ${uploadedDocs.length + 1}`);
      }
    }, 'image/jpeg', 0.8);
    
    stopCamera();
  };

  const handleFileSelect = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    for (const file of files) {
      const title = file.name.split('.')[0] || `Document ${uploadedDocs.length + 1}`;
      await uploadDocument(file, title);
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadDocument = async (file, title) => {
    if (uploadedDocs.length >= 5) {
      toast.error('Maximum 5 documents par consultation');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await consultationDocumentsAPI.upload(formData, title);
      
      setUploadedDocs(prev => [...prev, response.data]);
      toast.success('Document uploadé');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const removeDocument = async (docId) => {
    try {
      await consultationDocumentsAPI.delete(docId);
      setUploadedDocs(prev => prev.filter(d => d.id !== docId));
      toast.success('Document supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCompleteAppointment = async () => {
    if (!summaryForm.diagnosis.trim()) {
      toast.error('Le diagnostic est obligatoire');
      return;
    }
    
    if (summaryForm.diagnosis.trim().length < 5) {
      toast.error('Le diagnostic doit contenir au moins 5 caractères');
      return;
    }

    setCompleting(true);
    try {
      const summaryData = {
        diagnosis: summaryForm.diagnosis.trim(),
        recommendations: summaryForm.recommendations.trim() || null,
        treatment: summaryForm.treatment.trim() || null,
        next_appointment: summaryForm.next_appointment || null,
        private_notes: summaryForm.private_notes.trim() || null,
        document_ids: uploadedDocs.map(d => d.id)
      };

      await appointmentsAPI.completeWithSummary(selectedAppointment.id, summaryData);
      
      toast.success('Consultation terminée ! Un récapitulatif a été envoyé au patient.');
      setShowCompleteModal(false);
      loadAppointments();
    } catch (error) {
      console.error('Error completing appointment:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la finalisation');
    } finally {
      setCompleting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-amber-100 text-amber-800', label: 'En attente' },
      confirmed: { bg: 'bg-blue-100 text-blue-800', label: 'Confirmé' },
      in_progress: { bg: 'bg-purple-100 text-purple-800', label: 'En cours' },
      completed: { bg: 'bg-emerald-100 text-emerald-800', label: 'Terminé' },
      cancelled: { bg: 'bg-red-100 text-red-800', label: 'Annulé' }
    };
    const style = styles[status] || styles.pending;
    return <Badge className={style.bg}>{style.label}</Badge>;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video_call': return Video;
      case 'home_visit': return Home;
      default: return MapPin;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'video_call': return 'Téléconsultation';
      case 'home_visit': return 'Domicile';
      default: return 'Consultation';
    }
  };

  const filteredAppointments = appointments.filter(apt => {
    if (filter === 'all') return true;
    return apt.status === filter;
  });

  // Sort: pending first, then confirmed, then by date
  const sortedAppointments = [...filteredAppointments].sort((a, b) => {
    const statusOrder = { pending: 0, confirmed: 1, in_progress: 2, completed: 3, cancelled: 4 };
    if (statusOrder[a.status] !== statusOrder[b.status]) {
      return statusOrder[a.status] - statusOrder[b.status];
    }
    return new Date(b.date) - new Date(a.date);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-[#E53935] animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 lg:px-8 py-6" data-testid="professional-appointments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Mes rendez-vous
          </h1>
          <p className="text-slate-500 mt-1">
            Gérez vos consultations et disponibilités
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="appointments" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-100 p-1 rounded-xl h-auto">
          <TabsTrigger 
            value="appointments" 
            className="rounded-lg py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="tab-appointments"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Rendez-vous
          </TabsTrigger>
          <TabsTrigger 
            value="availability" 
            className="rounded-lg py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm"
            data-testid="tab-availability"
          >
            <Settings className="w-4 h-4 mr-2" />
            Disponibilités
          </TabsTrigger>
        </TabsList>

        {/* Appointments Tab */}
        <TabsContent value="appointments" className="mt-0">
          {/* Filter Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {[
              { value: 'all', label: 'Tous', count: appointments.length },
              { value: 'pending', label: 'En attente', count: appointments.filter(a => a.status === 'pending').length },
              { value: 'confirmed', label: 'Confirmés', count: appointments.filter(a => a.status === 'confirmed').length },
              { value: 'completed', label: 'Terminés', count: appointments.filter(a => a.status === 'completed').length }
            ].map(tab => (
              <Button
                key={tab.value}
                variant={filter === tab.value ? 'default' : 'outline'}
                onClick={() => setFilter(tab.value)}
                className={`rounded-full ${filter === tab.value ? 'bg-[#E53935] hover:bg-[#C62828]' : ''}`}
                data-testid={`filter-${tab.value}`}
              >
                {tab.label} ({tab.count})
              </Button>
            ))}
          </div>

          {/* Appointments List */}
          {sortedAppointments.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="text-center py-12">
                <Calendar className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">Aucun rendez-vous</h3>
                <p className="text-slate-500 mt-2">
                  {filter === 'all' 
                    ? "Vous n'avez pas encore de rendez-vous"
                    : `Aucun rendez-vous avec le statut "${filter}"`}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {sortedAppointments.map((appointment) => {
                const TypeIcon = getTypeIcon(appointment.appointment_type);
                const canComplete = ['confirmed', 'in_progress'].includes(appointment.status);
                const canConfirm = appointment.status === 'pending';
                
                return (
                  <Card key={appointment.id} className="border-0 shadow-sm hover:shadow-md transition-all" data-testid={`appointment-${appointment.id}`}>
                    <CardContent className="p-5">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        {/* Patient Info */}
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <User className="w-7 h-7 text-slate-500" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-slate-900 text-lg">{appointment.patient_name}</h3>
                              {getStatusBadge(appointment.status)}
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {appointment.date}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {appointment.time_slot}
                              </span>
                              <span className="flex items-center gap-1">
                                <TypeIcon className="w-4 h-4" />
                                {getTypeLabel(appointment.appointment_type)}
                              </span>
                            </div>
                            {appointment.notes && (
                              <p className="text-sm text-slate-500 mt-2 line-clamp-2 bg-slate-50 p-2 rounded-lg">
                                <strong>Notes:</strong> {appointment.notes}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 ml-auto">
                          {/* View patient medical record */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedPatientForRecord({
                                id: appointment.patient_id,
                                name: appointment.patient_name,
                                appointmentId: appointment.id
                              });
                              setShowPatientRecord(true);
                            }}
                            className="border-[#1976D2] text-[#1976D2] hover:bg-blue-50"
                            data-testid={`view-record-${appointment.id}`}
                          >
                            <FolderOpen className="w-4 h-4 mr-1" />
                            Dossier
                          </Button>
                          
                          {canConfirm && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleStatusUpdate(appointment.id, 'cancelled')}
                                className="border-red-200 text-red-600 hover:bg-red-50"
                                data-testid={`cancel-${appointment.id}`}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Refuser
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleStatusUpdate(appointment.id, 'confirmed')}
                                className="bg-emerald-500 hover:bg-emerald-600"
                                data-testid={`confirm-${appointment.id}`}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Accepter
                              </Button>
                            </>
                          )}
                          
                          {canComplete && (
                            <Button
                              size="sm"
                              onClick={() => openCompleteModal(appointment)}
                              className="bg-[#1976D2] hover:bg-[#1565C0]"
                              data-testid={`complete-${appointment.id}`}
                            >
                              <ClipboardList className="w-4 h-4 mr-1" />
                              Terminer la consultation
                            </Button>
                          )}
                          
                          {appointment.appointment_type === 'video_call' && appointment.status === 'confirmed' && (
                            <Button
                              size="sm"
                              onClick={() => navigate(`/video-call/${appointment.id}`)}
                              className="bg-purple-500 hover:bg-purple-600"
                            >
                              <Video className="w-4 h-4 mr-1" />
                              Rejoindre
                            </Button>
                          )}
                          
                          {appointment.status === 'completed' && appointment.consultation_summary_id && (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Récap envoyé
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Availability Tab */}
        <TabsContent value="availability" className="mt-0">
          <AvailabilityManager />
        </TabsContent>
      </Tabs>

      {/* Complete Appointment Modal */}
      <Dialog open={showCompleteModal} onOpenChange={setShowCompleteModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#1976D2]" />
              Résumé de consultation
            </DialogTitle>
            <DialogDescription>
              Complétez le résumé pour terminer la consultation. Un récapitulatif sera envoyé au patient par email.
            </DialogDescription>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="space-y-5">
              {/* Appointment Info */}
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#E53935] flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{selectedAppointment.patient_name}</p>
                    <p className="text-sm text-slate-500">
                      {selectedAppointment.date} à {selectedAppointment.time_slot}
                    </p>
                  </div>
                </div>
              </div>

              {/* Diagnosis (Required) */}
              <div>
                <Label className="text-slate-700 font-medium">
                  Diagnostic / Observations <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  value={summaryForm.diagnosis}
                  onChange={(e) => setSummaryForm(prev => ({ ...prev, diagnosis: e.target.value }))}
                  placeholder="Décrivez vos observations et le diagnostic..."
                  rows={3}
                  className="mt-2"
                  data-testid="summary-diagnosis"
                />
                <p className="text-xs text-slate-400 mt-1">Minimum 5 caractères. Ce champ est visible par le patient.</p>
              </div>

              {/* Recommendations */}
              <div>
                <Label className="text-slate-700 font-medium">Recommandations</Label>
                <Textarea
                  value={summaryForm.recommendations}
                  onChange={(e) => setSummaryForm(prev => ({ ...prev, recommendations: e.target.value }))}
                  placeholder="Conseils et recommandations pour le patient..."
                  rows={2}
                  className="mt-2"
                  data-testid="summary-recommendations"
                />
              </div>

              {/* Treatment */}
              <div>
                <Label className="text-slate-700 font-medium">Traitement prescrit</Label>
                <Textarea
                  value={summaryForm.treatment}
                  onChange={(e) => setSummaryForm(prev => ({ ...prev, treatment: e.target.value }))}
                  placeholder="Médicaments, posologie, durée..."
                  rows={2}
                  className="mt-2"
                  data-testid="summary-treatment"
                />
              </div>

              {/* Next Appointment */}
              <div>
                <Label className="text-slate-700 font-medium">Prochain rendez-vous conseillé</Label>
                <Select
                  value={summaryForm.next_appointment}
                  onValueChange={(value) => setSummaryForm(prev => ({ ...prev, next_appointment: value }))}
                >
                  <SelectTrigger className="mt-2" data-testid="summary-next-appointment">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {NEXT_APPOINTMENT_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Documents Upload Section */}
              <div>
                <Label className="text-slate-700 font-medium flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Documents & Ordonnances
                  <Badge variant="outline" className="text-xs">Optionnel</Badge>
                </Label>
                <p className="text-xs text-slate-400 mt-1 mb-3">
                  Photographiez ou uploadez des ordonnances, prescriptions. Max 5 documents.
                </p>
                
                {/* Camera View */}
                {showCamera && (
                  <div className="relative bg-black rounded-xl overflow-hidden mb-4">
                    <video 
                      ref={videoRef} 
                      autoPlay 
                      playsInline 
                      className="w-full h-48 object-cover"
                    />
                    <canvas ref={canvasRef} className="hidden" />
                    <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={stopCamera}
                        className="bg-white/90"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Annuler
                      </Button>
                      <Button
                        size="sm"
                        onClick={capturePhoto}
                        className="bg-[#E53935] hover:bg-[#C62828]"
                      >
                        <Camera className="w-4 h-4 mr-1" />
                        Capturer
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Upload Buttons */}
                {!showCamera && (
                  <div className="flex gap-2 mb-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={startCamera}
                      disabled={uploading || uploadedDocs.length >= 5}
                      className="flex-1 border-dashed"
                      data-testid="camera-btn"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Prendre une photo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || uploadedDocs.length >= 5}
                      className="flex-1 border-dashed"
                      data-testid="upload-btn"
                    >
                      {uploading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Choisir un fichier
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,application/pdf"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                )}
                
                {/* Uploaded Documents List */}
                {uploadedDocs.length > 0 && (
                  <div className="space-y-2 bg-purple-50 rounded-lg p-3">
                    {uploadedDocs.map((doc) => (
                      <div 
                        key={doc.id}
                        className="flex items-center justify-between bg-white rounded-lg p-2 shadow-sm"
                      >
                        <div className="flex items-center gap-2">
                          {doc.file_type === 'image' ? (
                            <div className="w-10 h-10 rounded bg-purple-100 overflow-hidden">
                              <img src={doc.file_url} alt={doc.title} className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div className="w-10 h-10 rounded bg-purple-100 flex items-center justify-center">
                              <FileText className="w-5 h-5 text-purple-600" />
                            </div>
                          )}
                          <span className="text-sm font-medium text-slate-700 truncate max-w-[150px]">
                            {doc.title}
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeDocument(doc.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Private Notes */}
              <div>
                <Label className="text-slate-700 font-medium flex items-center gap-2">
                  Notes privées
                  <Badge variant="outline" className="text-xs">Non visible par le patient</Badge>
                </Label>
                <Textarea
                  value={summaryForm.private_notes}
                  onChange={(e) => setSummaryForm(prev => ({ ...prev, private_notes: e.target.value }))}
                  placeholder="Notes internes pour votre usage personnel..."
                  rows={2}
                  className="mt-2 bg-amber-50 border-amber-200"
                  data-testid="summary-private-notes"
                />
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Email de récapitulatif</p>
                  <p className="text-blue-600">
                    Un email contenant le diagnostic, les recommandations, le traitement
                    {uploadedDocs.length > 0 && ` et ${uploadedDocs.length} document(s)`} sera automatiquement envoyé au patient.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => { setShowCompleteModal(false); stopCamera(); }}
                  className="flex-1"
                  disabled={completing}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleCompleteAppointment}
                  disabled={completing || !summaryForm.diagnosis.trim() || uploading}
                  className="flex-1 bg-[#1976D2] hover:bg-[#1565C0]"
                  data-testid="submit-summary"
                >
                  {completing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Terminer et envoyer
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Patient Medical Record Modal */}
      <Dialog open={showPatientRecord} onOpenChange={setShowPatientRecord}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-[#1976D2]" />
              Dossier médical - {selectedPatientForRecord?.name}
            </DialogTitle>
            <DialogDescription>
              Consultez et complétez le dossier de votre patient
            </DialogDescription>
          </DialogHeader>
          
          {selectedPatientForRecord && (
            <PatientMedicalRecord
              patientId={selectedPatientForRecord.id}
              appointmentId={selectedPatientForRecord.appointmentId}
              onClose={() => setShowPatientRecord(false)}
              mode="inline"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfessionalAppointments;
